import React from 'react';

/** Sentinel answers that count as a completed required field */
export const ANSWERED_SENTINELS = new Set([
  'unknown',
  'unsure',
  'n/a',
  'na',
  'none',
  'restricted',
]);

export const LOGISTICS_SERVICES = [
  'Electric',
  'Water',
  'Cable',
  'Sanitation',
  'Security',
  'Grounds',
  'Irrigation',
  'HVAC/Generators',
  'Pool',
  'Oil',
  'Propane/Gas',
  'Sewer or Septic',
  'Plowing/Snow Removal',
] as const;

export type LogisticsRow = {
  company: string;
  cost: string;
  frequency: string;
  na?: boolean;
};

/** HOA detail fields that are only required when has_hoa is Yes */
export const CONDITIONAL_HOA_FIELDS = [
  'association_name',
  'hoa_fee',
  'hoa_fee_frequency',
  'pets_allowed',
] as const;

export function isEmptyValue(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  // Booleans and numbers (including 0 / false) are explicit answers
  if (typeof value === 'boolean' || typeof value === 'number') return false;
  if (Array.isArray(value)) return value.length === 0;
  return !String(value).trim();
}

export function isYes(value: unknown): boolean {
  return value === true || value === 1 || value === 'true' || value === 'yes';
}

export function isNo(value: unknown): boolean {
  return value === false || value === 0 || value === 'false' || value === 'no';
}

export function parseUtilitiesTable(
  renovations: Record<string, unknown> | null | undefined
): Record<string, LogisticsRow> {
  if (!renovations) return {};
  const live = renovations.utilities_table;
  if (live && typeof live === 'object' && !Array.isArray(live)) {
    return live as Record<string, LogisticsRow>;
  }
  const raw = renovations.utilities_vendors || renovations.average_costs;
  if (typeof raw !== 'string' || !raw.trim()) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

export function isLogisticsRowComplete(row: LogisticsRow | undefined): boolean {
  if (!row) return false;
  if (row.na) return true;
  const hasCompany = !!row.company?.trim();
  const hasCost = !!row.cost?.trim();
  const hasFrequency = !!row.frequency?.trim();
  return hasCompany && hasCost && hasFrequency;
}

export function RequiredMark() {
  return (
    <span className="text-brand-coral ml-0.5" aria-hidden="true">
      *
    </span>
  );
}
